/**
 * Card components for the GRN Allocation Repair page.
 *
 * Extracted from ``GrnAllocationRepair.tsx`` so both files stay under the
 * project's 500-line ceiling.
 *
 *  - ``GrnCard`` is the read-only card body shown for one GRN allocation.
 *  - ``DraggableGrn`` wraps it with dnd-kit draggable + per-card quick
 *    action buttons (move-to-line-0, line dropdown, split).
 *  - ``ScheduleDropZone`` is the schedule-row drop target.
 *
 * Drag identifiers are pass-through strings (``alloc-<id>`` or
 * ``grn-<id>``) so the page can route move calls to the right backend
 * endpoint without each card knowing which one to call.
 */
import { useDraggable, useDroppable } from '@dnd-kit/core';
import type { RepairGRN, RepairSchedule } from '../../lib/api/grn-repair';
import ManualAgreementBadge from '../../components/purchasing/ManualAgreementBadge';

export function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/** Browser-local today as yyyy-mm-dd — the same clock the Arrears Repair list cuts off on. */
export function localTodayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** A true arrear: a real, open slot (not shipped / cancelled) whose date has passed with balance still above zero. */
export function isArrearSchedule(s: RepairSchedule, todayIso: string): boolean {
  return !s.is_synthetic && s.status !== 'cancelled' && s.status !== 'shipped' && !!s.schedule_date
    && s.schedule_date < todayIso && parseFloat(s.balance_qty) > 0;
}

export function recomputeVerdict(
  receipt: string, schedDate: string | null,
  win: { days_early: number; days_late: number } | null,
): string {
  if (!schedDate || !win) return '—';
  const r = new Date(receipt + 'T00:00:00Z').getTime();
  const s = new Date(schedDate + 'T00:00:00Z').getTime();
  const diff = Math.round((r - s) / (1000 * 60 * 60 * 24));
  if (diff > win.days_late) return 'late';
  if (diff < -win.days_early) return 'early';
  return 'on_time';
}

export function verdictColor(v: string): string {
  if (v === 'late') return 'text-red-600 font-semibold';
  if (v === 'early') return 'text-amber-600';
  if (v === 'on_time') return 'text-green-600';
  return 'text-muted-foreground';
}

export function GrnCard({ grn, qtyOnSlot, schedDate, win, isDragging, isOverDelivery }: {
  grn: RepairGRN; qtyOnSlot?: string; schedDate: string | null;
  win: { days_early: number; days_late: number } | null;
  isDragging?: boolean; isOverDelivery?: boolean;
}) {
  const verdict = recomputeVerdict(grn.receipt_date, schedDate, win);
  const diffDays = schedDate
    ? Math.round((new Date(grn.receipt_date).getTime() - new Date(schedDate).getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const total = grn.quantity_received;
  const showSplit = qtyOnSlot !== undefined && qtyOnSlot !== total;
  return (
    <div className={`border rounded p-2 bg-white shadow-sm w-[200px] ${isDragging ? 'opacity-50' : ''} ${isOverDelivery ? 'border-amber-400' : ''}`}>
      <div className="text-xs font-mono text-muted-foreground">#{grn.id}{isOverDelivery && ' ⚠'}</div>
      <div className="text-sm">{fmtDate(grn.receipt_date)}</div>
      <div className="text-xs">
        qty {qtyOnSlot ?? total}
        {showSplit && <span className="text-muted-foreground"> of {total}</span>}
      </div>
      <div className={`text-xs ${verdictColor(verdict)}`}>
        {verdict} {diffDays !== null && schedDate ? `(${diffDays > 0 ? '+' : ''}${diffDays}d)` : ''}
        {isOverDelivery && <span className="text-amber-600"> · over</span>}
      </div>
    </div>
  );
}

const stopDrag = (e: React.SyntheticEvent) => { e.stopPropagation(); };

export function DraggableGrn({
  dragId, grn, qtyOnSlot, isOverDelivery, schedDate, win, schedules,
  onMove, onSplit, onDelete, currentSchedId, canSplit,
}: {
  dragId: string;
  grn: RepairGRN; qtyOnSlot?: string; isOverDelivery?: boolean;
  schedDate: string | null; win: { days_early: number; days_late: number } | null;
  schedules: RepairSchedule[];
  onMove: (schedId: number) => void;
  onSplit?: () => void;
  onDelete?: () => void;
  currentSchedId: number | null; canSplit: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: dragId });
  const line0 = schedules.find((s) => s.is_synthetic);
  const numbered = schedules.filter((s) => !s.is_synthetic);
  return (
    <div ref={setNodeRef} className="relative group">
      <div {...listeners} {...attributes} className="cursor-grab active:cursor-grabbing">
        <GrnCard grn={grn} qtyOnSlot={qtyOnSlot} schedDate={schedDate} win={win} isDragging={isDragging} isOverDelivery={isOverDelivery} />
      </div>
      <div
        className="flex flex-wrap gap-1 mt-1"
        onPointerDown={stopDrag} onMouseDown={stopDrag} onClick={stopDrag}
      >
        {line0 && currentSchedId !== line0.id && (
          <button
            className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 hover:bg-slate-300"
            title="Move to line 0 (synthetic anchor)"
            onClick={() => onMove(line0.id)}
          >→ 0</button>
        )}
        <select
          className="text-[10px] px-1 py-0.5 rounded border bg-white hover:bg-slate-50 max-w-[120px]"
          value=""
          onChange={(e) => {
            const sid = Number(e.target.value);
            if (sid) onMove(sid);
            e.target.value = '';
          }}
        >
          <option value="">→ line…</option>
          {numbered.filter((s) => s.id !== currentSchedId).map((s) => (
            <option key={s.id} value={s.id}>
              line {s.schedule_line_number} / {fmtDate(s.schedule_date)}
            </option>
          ))}
        </select>
        {canSplit && onSplit && (
          <button
            className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700"
            title="Split this allocation into two pieces on the same schedule"
            onClick={onSplit}
          >Split</button>
        )}
        {onDelete && (
          <button
            className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 hover:bg-red-200 text-red-700"
            title="Delete this allocation row (the schedule's shipped_qty drops by this qty)"
            onClick={onDelete}
          >✕</button>
        )}
      </div>
    </div>
  );
}

/** Helix vs ERP balance for a slot, ERP value emphasised: green when they agree, red when they differ. */
function ErpBalanceTag({ sched }: { sched: RepairSchedule }) {
  const helixBal = parseFloat(sched.balance_qty);
  if (sched.erp_balance_qty == null) {
    return (
      <div className="inline-flex items-center rounded-md border bg-white px-3 py-1.5 text-sm text-muted-foreground"
        title="The ERP does not report a balance for this slot">
        Helix balance <span className="ml-1.5 font-semibold text-foreground tabular-nums">{helixBal}</span>
        <span className="mx-2 text-slate-300">|</span>ERP balance <span className="ml-1.5 font-semibold tabular-nums">—</span>
      </div>
    );
  }
  const erpBal = parseFloat(sched.erp_balance_qty);
  const matches = helixBal === erpBal;
  return (
    <div className={`inline-flex items-stretch overflow-hidden rounded-md border text-sm ${matches ? 'border-green-400' : 'border-red-400'}`}
      title={matches ? 'Helix balance matches the ERP balance' : 'Helix balance differs from the ERP balance'}>
      <div className="flex items-center gap-1.5 bg-white px-3 py-1.5">
        <span className="text-muted-foreground">Helix balance</span>
        <span className="font-semibold tabular-nums">{helixBal}</span>
      </div>
      <div className={`flex items-center gap-1.5 px-3 py-1.5 ${matches ? 'bg-green-100 text-green-900' : 'bg-red-100 text-red-900'}`}>
        <span>ERP balance</span>
        <span className="text-lg font-bold leading-none tabular-nums">{erpBal}</span>
        <span className="font-bold">{matches ? '✓' : '≠'}</span>
      </div>
    </div>
  );
}

export function ScheduleDropZone({ sched, children, isActive, onCancel, onUncancel, onEditQty, onCloseHeld, showErp, highlightArrear }: {
  sched: RepairSchedule; children: React.ReactNode; isActive?: boolean;
  onCancel?: () => void; onUncancel?: () => void; onEditQty?: () => void;
  /** Close the remainder of a manually agreed slot (receipts kept). */
  onCloseHeld?: () => void;
  /** Show the ERP balance beside Helix's, with a match mark. */
  showErp?: boolean;
  /** Mark this slot as a true arrear (Arrears Repair view). */
  highlightArrear?: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `sched-${sched.id}` });
  const isCancelled = sched.status === 'cancelled';
  const filled = parseFloat(sched.allocated_qty);
  const cap = parseFloat(sched.schedule_qty);
  const fillState = cap === 0 ? 'empty' : filled > cap ? 'over' : filled === cap ? 'full' : filled > 0 ? 'partial' : 'empty';
  const bgClass = isCancelled ? 'bg-slate-100 opacity-70' : {
    empty: 'bg-slate-50',
    partial: 'bg-amber-50',
    full: 'bg-green-50',
    over: 'bg-red-50',
  }[fillState];
  const dropClass = isOver ? 'ring-2 ring-blue-500' : '';
  return (
    <div ref={setNodeRef} id={`sched-zone-${sched.id}`}
      className={`border rounded-md p-3 mb-3 ${bgClass} ${dropClass} ${isActive ? 'border-blue-500' : ''} ${highlightArrear ? 'border-2 border-red-600 shadow-[0_0_0_3px_rgba(220,38,38,0.15)]' : ''}`}>
      <div className="flex justify-between items-baseline mb-2">
        <div className="flex gap-3 items-baseline">
          {highlightArrear && (
            <span className="self-center rounded bg-red-600 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-white">
              Arrear · balance {parseFloat(sched.balance_qty)}
            </span>
          )}
          <div className={`font-mono text-sm ${isCancelled ? 'line-through text-muted-foreground' : ''}`}>Line {sched.schedule_line_number}</div>
          <div className="text-sm">{fmtDate(sched.schedule_date)}</div>
          {!isCancelled && <ManualAgreementBadge schedule={sched} className="self-center" />}
          <div className="text-xs text-muted-foreground">
            qty {sched.allocated_qty}/{sched.schedule_qty}
            {!isCancelled && fillState === 'over' && ' ⚠ OVER'}
            {!isCancelled && fillState === 'full' && ' ✓'}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <div className={`text-xs uppercase tracking-wide ${isCancelled ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>{sched.status}</div>
          {/* Edit qty + Cancel: real (numbered, dated) schedules only.
              Hidden for the synthetic line-0 bucket and cancelled rows. */}
          {onEditQty && !sched.is_synthetic && !isCancelled && (
            <button
              className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-700"
              title="Edit this schedule line's demanded quantity"
              onClick={onEditQty}
            >Edit qty</button>
          )}
          {onCancel && !sched.is_synthetic && !isCancelled && (
            <button
              className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 hover:bg-red-200 text-red-700"
              title="Cancel this schedule line and transfer its GRNs to the active schedule"
              onClick={onCancel}
            >Cancel line</button>
          )}
          {/* Close line: Manually agreed slots only. Unlike Cancel, the receipts
              stay where they are — only the open remainder is closed. */}
          {onCloseHeld && sched.collab_hold && !sched.is_synthetic && !isCancelled && sched.status !== 'shipped' && (
            <button
              className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 hover:bg-violet-200 text-violet-800"
              title="Close the open remainder of this manually agreed line; receipts stay on it"
              onClick={onCloseHeld}
              data-testid="close-held-btn"
            >Close line</button>
          )}
          {/* Un-cancel: cancelled real rows only — reactivates the retired slot. */}
          {onUncancel && !sched.is_synthetic && isCancelled && (
            <button
              className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-700"
              title="Reactivate this cancelled schedule line"
              onClick={onUncancel}
            >Un-cancel</button>
          )}
        </div>
      </div>
      {/* A cancelled slot the ERP still reports open is itself a mismatch worth showing. */}
      {showErp && !sched.is_synthetic && (!isCancelled || parseFloat(sched.erp_balance_qty ?? '0') > 0) && (
        <div className="mb-2"><ErpBalanceTag sched={sched} /></div>
      )}
      <div className="flex flex-wrap gap-2 min-h-[40px]">{children}</div>
    </div>
  );
}
